import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { FaPen, FaTimes } from 'react-icons/fa';
import ItemEditModal from './ItemEditModal';

const API = process.env.REACT_APP_API_BASE_URL;

const ItemThumb = ({ itemId, alt, isDarkMode, version }) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [itemId, version]);
  return (
    <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden flex-shrink-0 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-100 border-gray-200'}`}>
      {!broken && itemId ? (
        <img
          src={`${API}/items/image/${itemId}${version ? `?v=${version}` : ''}`}
          alt={alt || 'Item'}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={() => setBroken(true)}
        />
      ) : (
        <div className={`w-full h-full flex items-center justify-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      )}
    </div>
  );
};

const ItemSelectionModal = ({ isOpen, onClose, onSelect, onItemUpdated, isDarkMode, user }) => {
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedItemIndex, setSelectedItemIndex] = useState(0);
  const [editItem, setEditItem] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const fetchItemData = useCallback(async (searchTerm = '', currentPage = 0, currentLimit = 10) => {
    try {
      setLoading(true);
      const [response, countResponse] = await Promise.all([
        axios.get(`${API}/items`, { params: { page: currentPage, limit: currentLimit, filter: searchTerm } }),
        axios.get(`${API}/items/count`, { params: { filter: searchTerm } }),
      ]);
      setFilteredItems(response.data || []);
      setTotalItems(countResponse.data?.count || 0);
      setSelectedItemIndex(0);
    } catch (error) {
      console.error('Error fetching items:', error);
      setFilteredItems([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    setFilter('');
    setPage(0);
    fetchItemData('', 0, limit);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, limit]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const t = setTimeout(() => {
      fetchItemData(filter, page, limit);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, page, limit, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (e) => {
      if (editOpen) return;
      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          setSelectedItemIndex((p) => Math.max(0, p - 1));
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedItemIndex((p) => Math.min(Math.max(filteredItems.length - 1, 0), p + 1));
          break;
        case 'Enter':
          if (filteredItems[selectedItemIndex]) {
            e.preventDefault();
            onSelect(filteredItems[selectedItemIndex]);
            onClose();
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
        default:
          break;
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, filteredItems, selectedItemIndex, onSelect, onClose, editOpen]);

  const handleSaved = (updatedItem) => {
    setFilteredItems((prev) => prev.map((it) => (String(it._id) === String(updatedItem._id) ? { ...it, ...updatedItem } : it)));
    if (onItemUpdated) onItemUpdated(updatedItem);
  };

  if (!isOpen) return null;

  const totalPages = Math.max(1, Math.ceil(totalItems / limit));

  return (
    <>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
        <div className={`flex flex-col w-full max-w-6xl max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-start justify-between gap-4 px-6 py-5 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 flex-shrink-0">
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-white flex items-center">
                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                Select Item
              </h3>
              <p className="text-blue-100 text-sm mt-1">Search, edit, or choose an item for your quotation</p>
            </div>
            <button onClick={onClose} className="flex-shrink-0 text-white/80 hover:text-white hover:bg-white/20 rounded-full p-2 transition-all duration-200" title="Close" aria-label="Close item selector">
              <FaTimes />
            </button>
          </div>

          <div className={`px-4 sm:px-6 py-4 border-b flex-shrink-0 ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={filter}
                  onChange={(e) => { setFilter(e.target.value); setPage(0); }}
                  placeholder="Search by SKU code, description, brand, category..."
                  className={`w-full pl-11 pr-10 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-white border-gray-300 text-gray-700 placeholder-gray-500'}`}
                  autoFocus
                />
                <svg className="absolute left-4 top-3.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {filter && (
                  <button onClick={() => { setFilter(''); setPage(0); }} className={`absolute right-3 top-3.5 text-gray-400 ${isDarkMode ? 'hover:text-gray-200' : 'hover:text-gray-600'}`} aria-label="Clear search">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              <div className="md:w-40">
                <select
                  value={limit}
                  onChange={(e) => { setLimit(Number(e.target.value)); setPage(0); }}
                  className={`border rounded-xl px-4 py-3 w-full text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-700'}`}
                >
                  <option value={5}>5 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                </select>
              </div>
            </div>
            <div className={`mt-3 text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {totalItems > 0 ? (
                <>
                  Showing <span className={isDarkMode ? 'text-gray-200 font-semibold' : 'text-gray-800 font-semibold'}>{filteredItems.length ? page * limit + 1 : 0}</span>
                  {' – '}
                  <span className={isDarkMode ? 'text-gray-200 font-semibold' : 'text-gray-800 font-semibold'}>{Math.min((page + 1) * limit, totalItems)}</span>
                  {' of '}
                  <span className={isDarkMode ? 'text-gray-200 font-semibold' : 'text-gray-800 font-semibold'}>{totalItems}</span>
                  {' items'}
                </>
              ) : 'No items'}
            </div>
          </div>

          <div className={`flex-1 overflow-y-auto custom-scrollbar px-4 sm:px-6 py-4 ${isDarkMode ? 'bg-gray-900/30' : 'bg-gray-50/60'}`}>
            {loading ? (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className={`rounded-xl border p-4 animate-pulse ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <div className="flex gap-3">
                      <div className={`w-16 h-16 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                      <div className="flex-1">
                        <div className={`h-4 w-1/3 rounded mb-3 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                        <div className={`h-3 w-2/3 rounded mb-3 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                        <div className="flex gap-2">
                          <div className={`h-8 w-20 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                          <div className={`h-8 w-20 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredItems.length !== 0 ? (
              <div className="space-y-3">
                {filteredItems.map((item, index) => (
                  <div
                    key={item._id || index}
                    onClick={() => { onSelect(item); onClose(); }}
                    className={`rounded-xl border p-4 cursor-pointer transition-all duration-200 ${isDarkMode ? 'bg-gray-800 border-gray-700 hover:border-blue-500' : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-md'} ${index === selectedItemIndex ? (isDarkMode ? 'ring-2 ring-blue-500/70 border-blue-500' : 'ring-2 ring-blue-400 border-blue-300') : ''}`}
                  >
                    <div className="flex gap-3 sm:gap-4">
                      <ItemThumb itemId={item._id} alt={item.skuCode} isDarkMode={isDarkMode} version={item.imageId || item.updatedAt || ''} />
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <div className={`font-bold text-base ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{item.skuCode}</div>
                          {item.series && (
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${isDarkMode ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-800'}`}>{item.series}</span>
                          )}
                        </div>
                        <div className={`text-sm mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{item.skuDescription}</div>
                        <div className="flex flex-wrap gap-1.5">
                          <span className={`px-2 py-1 rounded-md text-xs font-semibold ${isDarkMode ? 'bg-emerald-500/10 text-emerald-200' : 'bg-green-50 text-green-700'}`}>₹{Number(item.mrp || 0).toLocaleString('en-IN')}</span>
                          {item.brand?.name && (
                            <span className={`px-2 py-1 rounded-md text-xs font-medium ${isDarkMode ? 'bg-purple-500/10 text-purple-200' : 'bg-purple-50 text-purple-700'}`}>{item.brand.name}</span>
                          )}
                          {item.category?.name && (
                            <span className={`px-2 py-1 rounded-md text-xs font-medium ${isDarkMode ? 'bg-sky-500/10 text-sky-200' : 'bg-sky-50 text-sky-700'}`}>{item.category.name}</span>
                          )}
                          {item.hsnCode && (
                            <span className={`px-2 py-1 rounded-md text-xs font-medium ${isDarkMode ? 'bg-amber-500/10 text-amber-200' : 'bg-amber-50 text-amber-700'}`}>HSN {item.hsnCode}</span>
                          )}
                          {item.gstRate != null && (
                            <span className={`px-2 py-1 rounded-md text-xs font-medium ${isDarkMode ? 'bg-rose-500/10 text-rose-200' : 'bg-red-50 text-red-700'}`}>GST {item.gstRate}%</span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 flex-shrink-0 justify-center">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setEditItem(item); setEditOpen(true); }}
                          className={`px-3 py-2 rounded-lg text-xs font-semibold border flex items-center justify-center gap-1.5 transition-colors ${isDarkMode ? 'border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/10' : 'border-indigo-200 text-indigo-700 hover:bg-indigo-50'}`}
                        >
                          <FaPen className="w-3 h-3" /> Edit
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onSelect(item); onClose(); }}
                          className="px-4 py-2 rounded-lg text-xs font-semibold text-white bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 shadow-sm"
                        >
                          Select
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={`text-center py-16 rounded-xl border ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
                <svg className={`mx-auto h-16 w-16 mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
                <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>No items found</h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{filter ? 'Try adjusting your search terms' : 'No items available'}</p>
                {filter && (
                  <button onClick={() => { setFilter(''); setPage(0); }} className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium">Clear Search</button>
                )}
              </div>
            )}
          </div>

          {!loading && filteredItems.length > 0 && (
            <div className={`px-4 sm:px-6 py-4 border-t flex flex-col sm:flex-row justify-between items-center gap-3 flex-shrink-0 ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
              <div className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Page <span className={isDarkMode ? 'text-gray-200 font-semibold' : 'text-gray-800 font-semibold'}>{page + 1}</span> of <span className={isDarkMode ? 'text-gray-200 font-semibold' : 'text-gray-800 font-semibold'}>{totalPages}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className={`px-4 py-2 font-medium rounded-lg border disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={(page + 1) * limit >= totalItems}
                  className={`px-4 py-2 font-medium rounded-lg border disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                  Next
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <ItemEditModal
        isOpen={editOpen}
        itemId={editItem?._id}
        initialItem={editItem}
        isDarkMode={isDarkMode}
        user={user}
        onClose={() => { setEditOpen(false); setEditItem(null); }}
        onSaved={handleSaved}
      />
    </>
  );
};

export default ItemSelectionModal;
